<template>
  <AisInstantSearch
    v-if="!enableFilters"
    :search-client="searchStore.getAlgoliaClient()"
    :index-name="productStore.productIndex"
    :insights="true"
    :future="{
      preserveSharedStateOnUnmount: true,
      persistHierarchicalRootCount: true,
    }"
    :routing="false"
    :initial-ui-state="{
      [productStore.productIndex]: {
        query: '',
        page: 1,
        refinementList: {
          'categories.name': [categoryName],
        },
        sortBy: defaultSort,
      },
    }"
    class="container"
  >
    <AisConfigure :hits-per-page.camel="100" :filters="getAlgoliaFilters" />

    <div class="relative flex gap-6">
      <div class="w-full">
        <ProductsAndFiltersWrapper />
      </div>
    </div>
  </AisInstantSearch>

  <AisInstantSearch
    v-else
    :search-client="searchStore.getAlgoliaClient()"
    :index-name="productStore.productIndex"
    :insights="true"
    :future="{
      preserveSharedStateOnUnmount: true,
      persistHierarchicalRootCount: true,
    }"
    :routing="routing"
    class="container"
  >
    <AisConfigure :hits-per-page.camel="100" />

    <div class="relative flex gap-6">
      <div
        class="custom-slider sticky top-0 hidden max-h-dvh w-1/4 shrink-0 overflow-y-auto overflow-x-hidden pr-2 pt-2 lg:block xl:w-1/5"
      >
        <DesktopFilters />
      </div>
      <div class="w-full">
        <ProductsAndFiltersWrapper />
      </div>
    </div>
  </AisInstantSearch>
</template>

<script setup lang="ts">
import DesktopFilters from '@/pages/CategoryPage/Partials/DesktopFilters.vue';
import ProductsAndFiltersWrapper from '@/pages/CategoryPage/Partials/ProductsAndFiltersWrapper.vue';
import {useAlgoliaProductStore} from '@/stores/Category/AlgoliaProductStore';
import {useAlgoliaSearchStore} from '@/stores/Category/AlgoliaSearchStore';
import {history as historyRouter} from 'instantsearch.js/es/lib/routers';
import {singleIndex} from 'instantsearch.js/es/lib/stateMappings';
import {computed, onMounted} from 'vue';

const enableFilters = window.enable_algolia_filters;

const productStore = useAlgoliaProductStore();
const searchStore = useAlgoliaSearchStore();

const categoryId = window.category_id;
const categoryParentId = window.category_parent_id;
const categoryName = window.category_name;
const categoryUrlKey = window.category_url_key;
const defaultSort = `${productStore.productIndex}_popular_desc`;

const getAlgoliaFilters = computed(() => {
    return `categories.name:'${categoryName}' `;
});

const routing = {
  router: historyRouter({
    cleanUrlOnDispose: false,
  }),
  stateMapping: productStore.enableAlgoliaFilters
    ? {
        stateToRoute(
          uiState: Record<
            string,
            {
              query: string;
              page: number;
              refinementList: object;
              sortBy: string;
            }
          >
        ) {
          const indexUiState = uiState[productStore.productIndex];
          const { query, page, refinementList, sortBy } = indexUiState;
          return {
            q: query,
            page: page,
            sort: sortBy?.replace(`${productStore.productIndex}_`, ''),
            ...refinementList,
          };
        },
        routeToState(
          routeState: Record<
            string,
            { q: string; page: number; sort: string; [key: string]: unknown }
          >
        ) {
          const { q, page, sort, ...refinementList } = routeState;

          return {
            [productStore.productIndex]: {
              query: q,
              page: page,
              sortBy: sort
                ? `${productStore.productIndex}_${sort}`
                : defaultSort,
              refinementList: productStore.enableAlgoliaFilters
                ? {
                    'categories.name': refinementList['categories.name'] ?? [
                      categoryName,
                    ],
                    ...refinementList,
                  }
                : undefined,
            },
          };
        },
      }
    : singleIndex(productStore.productIndex),
};

onMounted(async () => {
  await productStore.retrieveAvailableFilters();
});
</script>
